import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, CircularProgress, Typography, Checkbox, FormControlLabel, FormHelperText } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

interface OfferDocumentModalProps {
  open: boolean;
  documentBlob: Blob | null;
  loading: boolean;
  error: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const OfferDocumentModal = ({
  open,
  documentBlob,
  loading,
  error,
  onCancel,
  onConfirm
}: OfferDocumentModalProps) => {
  const { t } = useTranslation();
  const [hasReadDocument, setHasReadDocument] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const documentReadSchema = z.object({
    hasRead: z.literal(true, {
      errorMap: () => ({ message: t('common:text.contractation_offer_document_read_required') })
    })
  });

  const handleConfirm = useCallback(() => {
    const result = documentReadSchema.safeParse({ hasRead: hasReadDocument });
    if (!result.success) {
      setValidationError(result.error.issues[0].message);
      return;
    }
    setValidationError(null);
    onConfirm();
  }, [hasReadDocument, onConfirm, t]);

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  // Cleanup object URL when dialog closes or blob changes
  useEffect(() => {
    if (!documentBlob) {
      setObjectUrl(null);
      return;
    }

    const nextObjectUrl = URL.createObjectURL(documentBlob);
    setObjectUrl(nextObjectUrl);

    return () => {
      URL.revokeObjectURL(nextObjectUrl);
    };
  }, [documentBlob]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setHasReadDocument(false);
      setValidationError(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        {t('common:text.contractation_offer_document_title')}
      </DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60vh',
              gap: 2
            }}
          >
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              {t('common:text.contractation_offer_document_loading')}
            </Typography>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60vh',
              gap: 2
            }}
          >
            <Typography variant="body1" color="error">
              {t('common:text.contractation_offer_document_error')}
            </Typography>
          </Box>
        )}

        {!loading && !error && objectUrl && (
          <>
            <Box
              sx={{
                width: '100%',
                height: 'min(60vh, 500px)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <iframe
                src={objectUrl}
                title={t('common:text.contractation_offer_document_title')}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasReadDocument}
                  onChange={(e) => {
                    setHasReadDocument(e.target.checked);
                    if (e.target.checked) setValidationError(null);
                  }}
                />
              }
              label={t('common:text.contractation_offer_document_read_confirmation')}
              sx={{ mt: 1, mb: -1 }}
            />
            {validationError && (
              <FormHelperText error>{validationError}</FormHelperText>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 0.5, sm: 0 },
          px: 3,
          pb: 2,
          pt: 1
        }}
      >
        <Button onClick={onCancel} disabled={loading}>
          {t('common:text.contractation_offer_document_cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
          disabled={loading || error}
        >
          {t('common:text.contractation_offer_document_confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferDocumentModal;
